import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { oauth2 } from 'fhirclient';
import Client from 'fhirclient/lib/Client';
import { fhirclient } from 'fhirclient/lib/types';
import { Observable, throwError } from 'rxjs';
import { LogService } from '../core/log.service';
import { IFHIRService } from './fhir-service.interface';
import { ContactInformation } from './model/contact-information';
import Patient = fhirclient.FHIR.Patient;

@Injectable()
export class FHIRService implements IFHIRService {
  private log: LogService = inject(LogService);
  private client?: Client;

  public async getClient(): Promise<Client> {
    try {
      this.client = this.client || (await oauth2.ready());
      return this.client;
    } catch (e: any) {
      this.handleError(e);
      throw e;
    }
  }

  public async getPatient(): Promise<Patient | undefined> {
    await this.getClient();
    try {
      if (this.client) {
        try {
          // get the patient from client context if available
          return this.client.patient.read();
        } catch (error) {
          console.error('Patient is not added to the client context');
          //fallback when the patient is not added to the client context
          const patientId = sessionStorage.getItem('patientId');
          console.log('patientId from sessionStorage', patientId);
          if (patientId) return this.client.request('/Patient/' + patientId);
        }
      }
      return undefined;
    } catch (e: any) {
      this.handleError(e);
      throw e;
    }
  }

  public async getEncounterId(): Promise<string | undefined | null> {
    await this.getClient();
    try {
      return this.client ? this.client.encounter.id : undefined;
    } catch (e: any) {
      this.handleError(e);
      throw e;
    }
  }

  public async getPatientContact(): Promise<ContactInformation[]> {
    const patient = await this.getPatient();

    if (!patient?.['telecom']) {
      return [];
    }

    // Filter by mobile phone( Purpose of contact point)
    let telecomPhones = patient['telecom'].filter((telecom: any) => telecom.system === 'phone');

    if (telecomPhones.length === 0) {
      return [];
    }

    telecomPhones = patient['telecom'].filter(
      (telecom: any) => telecom.system === 'phone' && telecom.use === 'mobile',
    );

    // In case, mobile is not registered, filter by home phone
    if (telecomPhones.length === 0) {
      telecomPhones = patient['telecom'].filter(
        (telecom: any) => telecom.system === 'phone' && telecom.use === 'home',
      );
    }

    // In case that none of the above exists filter by any other type
    if (telecomPhones.length === 0) {
      telecomPhones = patient['telecom'].filter((telecom: any) => telecom.system === 'phone');
    }

    const eContacts: ContactInformation[] = telecomPhones.map((telecom: any) => ({
      entityId: patient.id,
      name: patient['name'][0].text,
      contactNumber: `+1${telecom.value.replace(/-/g, '')}`,
      createdOn: Date.now(),
      isPatient: true,
      fromEpic: true,
      selfPatient: true,
      use: telecom.use,
    }));

    return eContacts;
  }

  public async getUserId(): Promise<string | null | undefined> {
    try {
      await this.getClient();
      return this.client?.user?.id;
    } catch (e: any) {
      this.handleError(e);
      throw e;
    }
  }

  public async searchLocation(locationId: string) {
    try {
      return await this.client?.request(`/Location/${locationId}`);
    } catch (e: any) {
      this.handleError(e);
    }
  }

  public async searchPractitionerRole(providerId: string) {
    await this.getClient();
    try {
      if (this.client) {
        const response = await this.client.request(`/PractitionerRole?practitioner=${providerId}`);
        const entry = response?.entry.find(
          (role: any) => role?.resource?.resourceType === 'PractitionerRole',
        );
        return entry?.resource;
      }
      return undefined;
    } catch (e: any) {
      this.handleError(e);
    }
  }

  public async getPatientEmergencyContactsInformation(): Promise<ContactInformation[]> {
    const patient = await this.getPatient();

    if (!patient?.['contact']) {
      return [];
    }

    try {
      return patient?.['contact'].reduce((acc: any, contact: any) => {
        const isEmergencyContact =
          contact.relationship?.some((rel: any) => {
            return rel.coding.some((coding: any) => coding.code === 'C');
          }) && contact.telecom?.some((telecom: any) => telecom.system === 'phone');
        if (isEmergencyContact) {
          const telecomPhones = contact.telecom.filter(
            (telecom: any) => telecom.system === 'phone',
          );
          const eContacts: ContactInformation[] = telecomPhones.map((telecom: any) => ({
            entityId: patient.id,
            name: patient['name'][0].text,
            contactNumber: `+1${telecom.value}`,
            createdOn: Date.now(),
            emergencyContactName: contact.name.text,
            isPatient: true,
            fromEpic: true,
          }));

          return acc.concat(eContacts);
        }

        return acc;
      }, []);
    } catch (e: any) {
      this.log.error(e.message, e);
      return [];
    }
  }

  public async getPractitionerContactsInformation(
    providerId: string,
  ): Promise<ContactInformation[]> {
    const location = await this.searchLocation(this.client?.state?.tokenResponse?.['location']);

    try {
      const telecomPhones = location?.telecom?.filter((telecom: any) => telecom.system === 'phone');
      if (!telecomPhones) {
        return [];
      }

      return telecomPhones.map((telecom: any) => ({
        entityId: providerId,
        locationId: location.id,
        name: location?.name,
        contactNumber: `+1${telecom.value}`,
        createdOn: Date.now(),
        isPatient: false,
        fromEpic: true,
      }));
    } catch (e: any) {
      this.log.error(e.message, e);
      return [];
    }
  }

  private handleError(err: Error | HttpErrorResponse): Observable<never> {
    this.log.error(err);
    return throwError(() => err);
  }
}
